import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { CharacterMarkerGroup } from "../../board/ChapterBoard/types";
import { characterGroupsAtPlayhead } from "../../character/ActiveCharacterGroup/atPlayhead";
import { locationRunRole } from "../LocationRun/branchKey";
import { createFrameMemo } from "../../rail/FrameMemo/memo";
import { partRunPhase } from "../../rail/PartRunPhase/phase";
import type { ActiveLocationGroup } from "./index";
import { branchIds, partPlaceId, runsOnBranch } from "./branchRuns";
import {
  buildCohortGroup,
  buildRailPartGroup,
  buildUnifiedBranchGroup,
} from "./cohort";

export function activeLocationGroupsAtPlayhead(
  layout: BookTimelineLayout,
  playheadMs: number,
): ActiveLocationGroup[] {
  return groupsMemo(layout, playheadMs, "", () => computeGroups(layout, playheadMs));
}

const groupsMemo = createFrameMemo<BookTimelineLayout, ActiveLocationGroup[]>();

function computeGroups(
  layout: BookTimelineLayout,
  playheadMs: number,
): ActiveLocationGroup[] {
  const cohorts = characterGroupsAtPlayhead(layout.rail, playheadMs);
  const cohortsByBranch = new Map<string, CharacterMarkerGroup[]>();
  for (const cohort of cohorts) {
    const list = cohortsByBranch.get(cohort.branchId) ?? [];
    list.push(cohort);
    cohortsByBranch.set(cohort.branchId, list);
  }

  const groups: ActiveLocationGroup[] = [];
  for (const branchId of branchIds(layout)) {
    const branchCohorts = cohortsByBranch.get(branchId) ?? [];
    if (branchCohorts.length <= 1) {
      const group = buildUnifiedBranchGroup(
        layout,
        branchId,
        playheadMs,
        branchCohorts[0],
      );
      if (group !== null) {
        groups.push(group);
      }
      continue;
    }
    const cohortPlaces = new Set(branchCohorts.map((cohort) => cohort.placeId));
    for (const cohort of branchCohorts) {
      const group = buildCohortGroup(layout, cohort, playheadMs);
      if (group !== null) {
        groups.push(group);
      }
    }
    for (const run of runsOnBranch(layout, branchId)) {
      if (locationRunRole(run) !== "secondary") {
        continue;
      }
      const phase = partRunPhase(layout.rail, run, playheadMs).kind;
      if (phase === "active" && cohortPlaces.has(partPlaceId(run))) {
        continue;
      }
      const group = buildRailPartGroup(layout, branchId, run, playheadMs);
      if (group !== null) {
        groups.push(group);
      }
    }
  }
  return groups;
}
